import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, MapPin, Clock, ArrowRight, Mail, Facebook, Instagram } from "lucide-react";
import Waves from "@/components/Waves";
import Seo from "@/components/Seo";
import { Bubbles } from "@/components/BackgroundFX";

const CARDS = [
  {
    icon: Phone,
    label: "Give us a bell",
    value: "0415 464 900",
    sub: "Tap to call — Paul answers",
    href: "tel:0415464900",
    testid: "contact-phone-card",
  },
  {
    icon: Mail,
    label: "Email us",
    value: "samiotismf@gmail.com",
    sub: "Tap to email — send your job details through",
    href: "mailto:samiotismf@gmail.com",
    testid: "contact-email-card",
    smallValue: true,
  },
  {
    icon: MapPin,
    label: "The workshop",
    value: "Albany, WA",
    sub: "Servicing the Great Southern Region",
    testid: "contact-location-card",
  },
  {
    icon: Clock,
    label: "Hours",
    value: "Mon – Fri, 7am – 5pm",
    sub: "Saturdays by appointment",
    testid: "contact-hours-card",
  },
  {
    icon: Facebook,
    label: "Facebook",
    value: "Sami Fab",
    sub: "Follow the latest builds",
    href: "https://www.facebook.com/share/1FPbwHzncB/?mibextid=wwXIfr",
    testid: "contact-facebook-card",
  },
  {
    icon: Instagram,
    label: "Instagram",
    value: "@samiotis.fab",
    sub: "Photos of jobs as they happen",
    href: "https://www.instagram.com/samiotis.fab",
    testid: "contact-instagram-card",
  },
];

const AREAS = ["Albany", "Denmark", "Mount Barker", "Katanning", "Walpole", "Esperance", "Great Southern"];

export default function ContactPage() {
  return (
    <>
      <Seo
        title="Contact SAMI FAB | Boat Modifications & Fabrication Albany WA"
        description="Contact SAMI FAB in Albany WA for boat modifications, aluminium fabrication, kit boat builds, marine maintenance, toolboxes, canopies, trailers and caravan accessories."
        keywords="contact aluminium fabricator Albany, boat modifications contact, marine fabrication Albany WA, SAMI FAB phone, fabrication quote Albany"
        path="/contact"
      />
      <section data-testid="contact-page-header" className="relative overflow-hidden pt-[76px] bg-white border-b border-[#007bff]/15">
        <Bubbles />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-14">
          <div className="overflow-hidden">
            <motion.p
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
              className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#007bff]"
            >
              Contact — Sami Fab, Albany WA
            </motion.p>
          </div>
          <div className="overflow-hidden mt-4">
            <motion.h1
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.22 }}
              className="font-display font-black uppercase tracking-tighter text-[#003366] text-4xl sm:text-5xl lg:text-6xl leading-[0.98]"
            >
              Talk to Paul.
            </motion.h1>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
            className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-slate-600"
          >
            One workshop, one fabricator — when you get in touch, you talk directly to the bloke
            who’ll be welding your job.
          </motion.p>
        </div>
      </section>

      {/* Big phone band */}
      <section className="relative bg-[#003366] overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-blue-300">Fastest way to reach us</p>
            <a
              href="tel:0415464900"
              data-testid="contact-big-phone"
              className="mt-4 inline-block font-display font-black tracking-tighter text-white text-5xl sm:text-6xl lg:text-7xl hover:text-[#4da3ff] transition-colors duration-200"
            >
              0415 464 900
            </a>
            <p className="mt-4 text-blue-100/80 text-base">Tap to call — if Paul’s under a boat, he’ll ring you back.</p>
          </motion.div>
        </div>
        <Waves front="#ffffff" back="rgba(77,163,255,0.4)" />
      </section>

      {/* Info cards */}
      <section className="relative bg-white overflow-hidden">
        <div className="grid-drift" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {CARDS.map((c, i) => {
              const inner = (
                <>
                  <span aria-hidden className="absolute top-0 left-0 h-[3px] w-10 bg-[#007bff] transition-[width] duration-300 group-hover:w-full" />
                  <span className="w-12 h-12 bg-[#003366] text-white flex items-center justify-center transition-colors duration-300 group-hover:bg-[#007bff]">
                    <c.icon size={22} strokeWidth={1.75} />
                  </span>
                  <p className="mt-5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{c.label}</p>
                  <p className={`mt-2 font-display font-black tracking-tight text-[#003366] break-all ${c.smallValue ? "text-lg leading-snug" : "text-2xl"}`}>{c.value}</p>
                  <p className="mt-1.5 text-sm text-slate-600">{c.sub}</p>
                </>
              );
              const cls =
                "group relative block bg-white border border-[#007bff]/45 p-7 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-[#007bff] hover:shadow-[0_24px_44px_-18px_rgba(0,51,102,0.3)]";
              return (
                <motion.div
                  key={c.testid}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 }}
                >
                  {c.href?.startsWith("/") ? (
                    <Link to={c.href} data-testid={c.testid} className={cls}>{inner}</Link>
                  ) : c.href?.startsWith("http") ? (
                    <a href={c.href} target="_blank" rel="noopener noreferrer" data-testid={c.testid} className={cls}>{inner}</a>
                  ) : c.href ? (
                    <a href={c.href} data-testid={c.testid} className={cls}>{inner}</a>
                  ) : (
                    <div data-testid={c.testid} className={cls}>{inner}</div>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Service area */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-16 border border-[#007bff]/25 bg-white p-7 md:p-9"
            data-testid="contact-service-area"
          >
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#007bff]">Where we work</p>
            <h2 className="font-display font-black uppercase tracking-tight text-2xl md:text-3xl text-[#003366] mt-3">
              Based in Albany — on the road across the Great Southern
            </h2>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {AREAS.map((a) => (
                <span
                  key={a}
                  className="font-mono text-[10px] md:text-xs font-bold uppercase tracking-[0.15em] border border-[#007bff]/30 text-[#003366] px-3.5 py-2"
                >
                  {a}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
            className="mt-12 flex flex-wrap justify-center gap-4"
          >
            <a
              href="tel:0415464900"
              data-testid="contact-call-now-button"
              className="inline-flex items-center gap-3 bg-[#343a40] text-white px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-[#007bff] hover:scale-[1.03] transition-[background-color,transform] duration-200"
            >
              <Phone size={16} /> Call Now
            </a>
            <Link
              to="/quote"
              data-testid="contact-quote-button"
              className="group inline-flex items-center gap-3 border border-[#343a40] text-[#343a40] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:border-[#007bff] hover:text-[#007bff] transition-colors duration-200"
            >
              Request a Quote
              <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
